import React, { useEffect, useState } from 'react';
import api from '../../utils/api';
import UniversityAnalytics from '../../Components/Dashboard/UniversityAnalytics';
import MajorAnalytics from '../../Components/Dashboard/MajorAnalytics';
import ProvincieInsight from '../../Components/Dashboard/ProvincieInsight';
import GenderAnalytics from '../../Components/Dashboard/GenderAnalytics';
import SectionCard from '../../Components/Table/SectionCard';

const SECTIONS = [
  { id: 'gender', icon: 'bi-gender-ambiguous', iconColor: 'primary', label: 'Gender & Usia' },
  { id: 'university', icon: 'bi-mortarboard-fill', iconColor: 'success', label: 'Universitas' },
  { id: 'major', icon: 'bi-diagram-3-fill', iconColor: 'warning', label: 'Program Studi' },
  { id: 'province', icon: 'bi-geo-alt-fill', iconColor: 'info', label: 'Provinsi' },
];

/**
 * Halaman gabungan untuk analitik demografi peserta (semi-strategis):
 * universitas, program studi, provinsi, dan gender. Sebelumnya masing-masing
 * punya route/page terpisah — digabung ke 1 halaman karena isinya saling
 * terkait (semua "siapa peserta kita") dan supaya tidak terlalu banyak
 * halaman navigasi terpisah untuk info yang sebenarnya berkaitan.
 */
export default function InsightsPage() {
  const [universities, setUniversities] = useState([]);
  const [majors, setMajors] = useState([]);
  const [domicilies, setDomicilies] = useState([]);
  const [genderData, setGenderData] = useState({
    countUsersByGender: [],
    countUsersByGenderOverTime: [],
    countUsersByAge: [],
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    const fetchAll = async () => {
      try {
        setError(false);
        const [universitiesRes, majorsRes, domiciliesRes, genderRes] = await Promise.all([
          api.analyticsUsersByUniversities(),
          api.analyticsUsersByMajor(),
          api.analyticsUsersByProvincies(),
          api.analyticsUsersByGender(),
        ]);

        setUniversities((universitiesRes || []).sort((a, b) => b.total_students - a.total_students));
        setMajors((majorsRes || []).sort((a, b) => b.total_users - a.total_users));
        setDomicilies((domiciliesRes || []).sort((a, b) => b.total_users - a.total_users));
        setGenderData({
          countUsersByGender: genderRes?.countUsersByGender || [],
          countUsersByGenderOverTime: genderRes?.countUsersByGenderOverTime || [],
          countUsersByAge: genderRes?.countUsersByAge || [],
        });
      } catch (err) {
        console.error('InsightsPage fetch error:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    fetchAll();
  }, []);

  const handleJump = (e, id) => {
    e.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <div className="container-fluid p-0">
      {/* PAGE HEADER */}
      <div className="d-flex align-items-center gap-3 mb-4">
        <div
          className="bg-primary bg-opacity-10 d-flex align-items-center justify-content-center rounded-3"
          style={{ width: '40px', height: '40px' }}
        >
          <i className="bi bi-graph-up-arrow text-primary fs-5"></i>
        </div>
        <div>
          <h1 className="h5 fw-bold mb-0 text-dark">Insight Peserta</h1>
          <p className="text-muted mb-0" style={{ fontSize: '0.75rem' }}>
            Ringkasan demografi peserta: gender, usia, asal universitas, program studi, dan provinsi.
          </p>
        </div>
      </div>

      {/* JUMP NAVIGATION -- membantu navigasi cepat karena halaman ini panjang */}
      <div
        className="d-flex flex-wrap gap-2 mb-4 py-2 bg-light bg-opacity-75"
        style={{ position: 'sticky', top: 0, zIndex: 5, backdropFilter: 'blur(4px)' }}
      >
        {SECTIONS.map((s) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            onClick={(e) => handleJump(e, s.id)}
            className="btn btn-sm rounded-pill px-3 fw-semibold border-0 bg-white shadow-sm text-dark"
          >
            <i className={`bi ${s.icon} me-1 text-${s.iconColor}`}></i>
            {s.label}
          </a>
        ))}
      </div>

      {error && (
        <div className="alert alert-danger rounded-4 border-0 shadow-sm d-flex align-items-center gap-2">
          <i className="bi bi-exclamation-triangle-fill"></i>
          Sebagian data gagal dimuat. Coba muat ulang halaman.
        </div>
      )}

      <SectionCard
        id="gender"
        icon="bi-gender-ambiguous"
        iconColor="primary"
        title="Gender & Usia"
        subtitle="Proporsi gender, sebaran usia, dan tren pendaftaran bulanan."
      >
        <GenderAnalytics data={genderData} loading={loading} />
      </SectionCard>

      <SectionCard
        id="university"
        icon="bi-mortarboard-fill"
        iconColor="success"
        title="Asal Universitas"
        subtitle="Kampus mana yang menyumbang peserta terbanyak."
      >
        <UniversityAnalytics universities={universities} loading={loading} showTable={false} showInsight={false} />
      </SectionCard>

      <SectionCard
        id="major"
        icon="bi-diagram-3-fill"
        iconColor="warning"
        title="Program Studi"
        subtitle="Jurusan/program studi asal peserta."
      >
        <MajorAnalytics majors={majors} loading={loading} showTable={false} />
      </SectionCard>

      <SectionCard
        id="province"
        icon="bi-geo-alt-fill"
        iconColor="info"
        title="Persebaran Provinsi"
        subtitle="Asal domisili peserta per provinsi."
      >
        <ProvincieInsight domicilies={domicilies} loading={loading} showTable={false} showInsight={false} />
      </SectionCard>
    </div>
  );
}
